import { useEffect } from 'react';

// "Curtain wipe" scroll animations: photos are revealed top-to-bottom, headings
// are written in left-to-right, and cards slide in from the right with a slight
// slant. Each element animates once, when its container scrolls into view.
// Skipped entirely for guests who prefer reduced motion (and in environments
// without IntersectionObserver), so the page simply renders as-is.

const HEADS = '.ed-title, .section-title, .around-main h2';
const CARDS =
  '.stay-route-stop, .menu-card, .postcard-slot, .curated-card, .around-picks-row li, .almanac-card, .accordion-item, .around-group';
const PHOTOS = '.around-home, .curated-card-arch, .around-picks-photo, .menu-card-photo, .postcard-photo, .house-cover, .site-footer-hero';

export function useCurtainReveal(): void {
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.IntersectionObserver !== 'function') return undefined;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return undefined;

    const root = document.documentElement;
    root.classList.add('fx-on');

    const groups = new Map<Element, Element[]>();
    // Clipped elements have no visible area, so observe their parent instead.
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          (groups.get(entry.target) ?? []).forEach((el) => el.classList.add('in'));
          groups.delete(entry.target);
          io.unobserve(entry.target);
        });
      },
      { threshold: 0, rootMargin: '0px 0px -10% 0px' },
    );

    const tag = (scope: ParentNode) => {
      const mark = (selector: string, cls: string) => {
        scope.querySelectorAll(selector).forEach((el) => {
          if (el.classList.contains(cls) || el.closest('#hero')) return;
          if (cls !== 'fx-photo' && el.parentElement?.closest('.fx-card')) return;
          el.classList.add(cls);
          const index = el.parentElement ? Array.from(el.parentElement.children).indexOf(el) : 0;
          (el as HTMLElement).style.setProperty('--fx-delay', `${Math.min(Math.max(index, 0), 6) * 0.1}s`);
          const watch = el.parentElement ?? el;
          if (!groups.has(watch)) {
            groups.set(watch, []);
            io.observe(watch);
          }
          groups.get(watch)!.push(el);
        });
      };
      // Headings use the line reveal (useTextReveal) instead of the wipe.
      if (!document.documentElement.classList.contains('tr-on')) mark(HEADS, 'fx-head');
      mark(CARDS, 'fx-card');
      mark(PHOTOS, 'fx-photo');
    };

    tag(document);
    // Lazy sections and re-rendered lists (category / neighborhood changes) mount later.
    let pending = 0;
    const mo = new MutationObserver(() => {
      if (pending) return;
      pending = window.setTimeout(() => {
        pending = 0;
        tag(document);
      }, 60);
    });
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      mo.disconnect();
      io.disconnect();
      if (pending) window.clearTimeout(pending);
      root.classList.remove('fx-on');
    };
  }, []);
}
